/**
 * Single source of truth for the partner snapshot.
 *
 * Hydrated by `PortalShell.onMounted` via `fetchOnce()`; consumed by
 * HomeView, CouponsView, PaymentsView, and the AccountPanel sub-views.
 * After an IBAN or email confirmation the API returns the new partner
 * payload — call `setPartner(p)` to replace the cached value
 * (avoids a refetch round-trip).
 *
 * `fetchOnce()` is idempotent: the first call hits the API; subsequent
 * ones are no-ops while `partner` is already populated. This prevents
 * the three /self refetches per portal navigation that the design
 * bundle's per-view `useEffect` chain would otherwise produce.
 */
import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { fetchSelf } from '@/api/portal'
import type { PortalSelfResponse } from '@/types/portal'

// Re-export so library organisms (`AccountPanel*`) that import the
// type from the store continue to resolve without churn.
export type { PortalSelfResponse }

export const usePortalSelfStore = defineStore('portalSelf', () => {
  const partner = ref<PortalSelfResponse | null>(null)
  const loading = ref(false)
  const error = ref(false)

  /**
   * Load the partner snapshot once. Idempotent — exits early while
   * `partner` is already populated or a request is in flight.
   */
  async function fetchOnce(): Promise<void> {
    if (partner.value !== null || loading.value) return
    loading.value = true
    error.value = false
    try {
      const r = await fetchSelf()
      partner.value = r.data
    } catch {
      error.value = true
    } finally {
      loading.value = false
    }
  }

  /** Replace the cached partner — used after IBAN / email confirm. */
  function setPartner(p: PortalSelfResponse): void {
    partner.value = p
    error.value = false
  }

  return { partner, loading, error, fetchOnce, setPartner }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePortalSelfStore, import.meta.hot))
}
